import React, { Suspense, lazy, useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import GlobalStyle from './styles/GlobalStyle';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import AppLayout from './components/AppLayout';
import InAppToastHost, { dispatchInAppToast } from './components/InAppToast';
import DeviceApprovalGate from './components/DeviceApprovalGate';
import { checkAppUpdate } from './service/appVersionService';
import { fetchMaster } from './service/adminSettingsService';
import { sendWebReadyOnce, isInRnWebView, sendNavState, onPushEvent, listenWebviewMessages, sendExitApp } from './bridge/webviewBridge';
import { runTopBackHandler } from './bridge/backStack';
import { detectDeviceType } from './service/deviceAccessService';

const MainPage = lazy(() => import('./pages/MainPage'));
const PropertySearchPage = lazy(() => import('./pages/PropertySearchPage'));
const UtilPage = lazy(() => import('./pages/UtilPage'));
const QuestPage = lazy(() => import('./pages/QuestPage'));
const MyPage = lazy(() => import('./pages/MyPage'));
const NoticePage = lazy(() => import('./pages/NoticePage'));
const ChatPage = lazy(() => import('./pages/ChatPage'));
const AdminPage = lazy(() => import('./pages/admin/AdminPage'));
const ChangePasswordPopup = lazy(() => import('./components/ChangePasswordPopup'));
// 개발 전용 시안 랩 (npm start 에서만 라우트가 생긴다)
const IssueLabPage = process.env.NODE_ENV === 'development' ? lazy(() => import('./pages/IssueLabPage')) : null;

function LoadingScreen() {
  return null;
}

// 모바일 기기 여부 — RN 앱(WebView) 또는 모바일 브라우저(안드로이드/아이폰/패드).
// PC 데스크톱 브라우저는 false → 기기 코드 인증 면제(아이디/패스워드만으로 입장).
function isMobileDevice() {
  if (typeof window === 'undefined') return false;
  if (isInRnWebView()) return true;
  const ua = navigator.userAgent || '';
  // iPadOS 13+ 는 UA가 Macintosh로 위장 → 터치 포인트로 iPad 판별 (PC로 오인 방지)
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1);
}

// 화면 켜둠 — 기기 절전(예: 30초)과 무관하게 앱이 떠 있는 동안 화면이 꺼지지 않게 유지.
// Wake Lock 은 페이지가 숨겨지면(visibilitychange) 자동 해제되므로 다시 보일 때 재요청한다.
function WakeLockKeeper() {
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
    let lock = null;
    let released = false;

    const acquire = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        lock = await navigator.wakeLock.request('screen');
        lock.addEventListener('release', () => { lock = null; });
      } catch (e) { /* 미지원/거부 시 무시 */ }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible' && !lock && !released) acquire();
    };

    acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVisible);
      if (lock) { try { lock.release(); } catch (e) {} lock = null; }
    };
  }, []);
  return null;
}

// 콘텐츠 보호 — 관리자 설정(settings/master)에 따라 우클릭/복사/이미지저장 차단.
// 관리자는 면제(매물 관리/복사 필요). a_rmouse/a_copy/a_imgsave === 'N' 이면 해당 동작 차단.
function SiteProtection() {
  const { user } = useAuth();
  useEffect(() => {
    if (!user || user.isAdmin) return undefined; // 비로그인/관리자는 보호 미적용
    let cancelled = false;
    let cleanup = null;
    fetchMaster().then((m) => {
      if (cancelled || !m) return;
      const offs = [];
      // 오른쪽 마우스 사용중지 → 우클릭 컨텍스트 메뉴 차단
      if (m.a_rmouse === 'N') {
        const onCtx = (e) => e.preventDefault();
        document.addEventListener('contextmenu', onCtx);
        offs.push(() => document.removeEventListener('contextmenu', onCtx));
      }
      // 글복사 사용중지 → 복사/선택 차단
      if (m.a_copy === 'N') {
        const onCopy = (e) => e.preventDefault();
        const onSelect = (e) => e.preventDefault();
        document.addEventListener('copy', onCopy);
        document.addEventListener('selectstart', onSelect);
        const prevUS = document.body.style.userSelect;
        const prevWUS = document.body.style.webkitUserSelect;
        document.body.style.userSelect = 'none';
        document.body.style.webkitUserSelect = 'none';
        offs.push(() => {
          document.removeEventListener('copy', onCopy);
          document.removeEventListener('selectstart', onSelect);
          document.body.style.userSelect = prevUS;
          document.body.style.webkitUserSelect = prevWUS;
        });
      }
      // 사진 저장 사용중지 → 이미지 드래그(끌어서 저장) 차단 (뷰어 버튼 숨김은 각 화면에서 처리)
      if (m.a_imgsave === 'N') {
        const onDrag = (e) => { if (e.target && e.target.tagName === 'IMG') e.preventDefault(); };
        document.addEventListener('dragstart', onDrag);
        offs.push(() => document.removeEventListener('dragstart', onDrag));
      }
      cleanup = () => offs.forEach((fn) => fn());
    }).catch(() => {});
    return () => { cancelled = true; if (cleanup) cleanup(); };
  }, [user]);
  return null;
}

function PrivateRoute({ children }) {
  const { user, loading, completePasswordChange } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.needPasswordChange) {
    return <Suspense fallback={null}><ChangePasswordPopup user={user} onComplete={completePasswordChange} /></Suspense>;
  }
  const content = <Suspense fallback={<LoadingScreen />}>{children}</Suspense>;
  // 관리자(승인해줄 사람)는 기기 인증 면제, 그 외 직원은 미승인 기기면 코드 게이트
  const isAdminUser = user.isAdmin || user.dept === '대표' || user.loginId === 'admin';
  if (isAdminUser) return content;
  // PC(데스크톱 브라우저)는 아이디/패스워드만으로 입장 — 기기 코드 인증은 모바일 기기(앱/폰/패드)에만 적용
  if (!isMobileDevice()) return content;
  return <DeviceApprovalGate>{content}</DeviceApprovalGate>;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login?redirect=admin" replace />;
  if (!user.isAdmin) return <Navigate to="/" replace />;
  return <Suspense fallback={<LoadingScreen />}>{children}</Suspense>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  if (loading) return null;
  if (user) {
    const redirect = searchParams.get('redirect');
    return <Navigate to={redirect === 'admin' ? '/admin' : '/'} replace />;
  }
  return children;
}

function VersionChecker() {
  const location = useLocation();
  const [toast, setToast] = useState(null);
  const [isPC, setIsPC] = useState(() => typeof window !== 'undefined' && window.innerWidth > 768);
  useEffect(() => {
    const run = () => checkAppUpdate({ onUpdate: (info) => setToast(info) });

    // 1) 페이지 이동/최초 마운트 시 체크
    run();

    // 2) 5분마다 주기 체크
    const interval = setInterval(run, 5 * 60 * 1000);

    // 3) 탭 복귀(visibilitychange) / 창 포커스 시 체크
    const onVisible = () => { if (document.visibilityState === 'visible') run(); };
    const onFocus = () => run();
    const onResize = () => setIsPC(window.innerWidth > 768);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    window.addEventListener('resize', onResize);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('resize', onResize);
    };
  }, [location.pathname]);
  if (!toast) return null;
  return (
    <div style={{
      position: 'fixed',
      bottom: 16,
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'rgba(255,255,255,0.96)',
      borderRadius: 999,
      padding: '6px 14px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      zIndex: 9999,
      border: '1px solid #E5E7EB',
      whiteSpace: 'nowrap',
      maxWidth: '92vw',
    }}>
      <span style={{
        fontSize: 13,
        color: '#6B7280',
        letterSpacing: '-0.2px',
      }}>시스템이 업데이트중입니다</span>
    </div>
  );
}

/** RN WebView 네비게이션 상태 동기화 */
function NavStateSync() {
  const location = useLocation();
  useEffect(() => {
    if (!isInRnWebView()) return;
    const isRoot = location.pathname === '/' || location.pathname === '/login';
    sendNavState({ isRoot, path: location.pathname, canGoBackInWeb: !isRoot });
  }, [location.pathname]);
  return null;
}

/** RN 하드웨어 백 버튼 처리 — 상황에 맞게 단계적으로 뒤로가기.
 *  1) 열려있는 팝업/모달이 있으면 그것부터 닫기
 *  2) 서브 페이지면 홈으로 이동
 *  3) 홈에서는 "한 번 더 누르면 종료" 후 2초 안에 다시 누르면 앱 종료 */
function BackController() {
  const navigate = useNavigate();
  const exitArmedRef = useRef(null);

  useEffect(() => {
    if (!isInRnWebView()) return undefined;

    const handleBack = () => {
      // 1) 열려있는 팝업/모달 우선 닫기
      if (runTopBackHandler()) return;

      // 2) 서브 페이지 → 홈으로
      const path = window.location.pathname;
      if (path !== '/' && path !== '/login') {
        navigate('/');
        return;
      }

      // 3) 홈 → 한 번 더 누르면 종료
      if (exitArmedRef.current) {
        clearTimeout(exitArmedRef.current);
        exitArmedRef.current = null;
        sendExitApp();
        return;
      }
      dispatchInAppToast({ title: '풀잎하우스', body: '한 번 더 누르면 종료됩니다.' });
      exitArmedRef.current = setTimeout(() => { exitArmedRef.current = null; }, 2000);
    };

    const off = listenWebviewMessages((msg) => {
      if (msg.type === 'BACK_REQUEST' || msg.type === 'APP_EXIT_REQUEST') handleBack();
    });
    return () => {
      off();
      if (exitArmedRef.current) clearTimeout(exitArmedRef.current);
    };
  }, [navigate]);

  return null;
}

/** RN WebView 브릿지 초기화 (WEB_READY + 포그라운드 푸시 + 알림 딥링크) */
function RnBridgeInit() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!isInRnWebView()) return;
    sendWebReadyOnce();
    const offPush = onPushEvent((msg) => {
      console.log('[RN→Web] PUSH_EVENT', msg);
    });
    // 알림 "터치" → PUSH_OPENED. 딥링크 링크로 이동 (예: /?menu=teamRoom&msg=... 또는 &toss=...)
    const offOpen = listenWebviewMessages((msg) => {
      if (msg?.type !== 'PUSH_OPENED') return;
      const link = msg.link || msg.payload?.link;
      if (link) navigate(link);
      else if (msg.roomId || msg.toss) {
        const q = msg.toss ? `toss=${msg.toss}` : `msg=${msg.messageId || ''}`;
        navigate(`/?menu=teamRoom&${q}`);
      }
    });
    return () => { offPush(); offOpen(); };
  }, [navigate]);
  return null;
}

/** 로그인 성공 직후 홈으로 네비게이션 되는 동안 살짝 홈이 보이는 현상 방지용 전역 오버레이 */
function LoginTransitionOverlay() {
  const { loginTransitionUser } = useAuth();
  if (!loginTransitionUser) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10001,
      background: '#0A0A0A',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'plhouseTxFadeOut 0.8s ease forwards',
      pointerEvents: 'none',
    }}>
      <style>{`@keyframes plhouseTxFadeOut {
        0%, 60% { opacity: 1; }
        100% { opacity: 0; }
      }`}</style>
    </div>
  );
}

function App() {
  // 패드(아이패드/안드로이드 태블릿) 기기면 html.pad-mode 부여 →
  // 폰용 하단 메뉴(BottomNav) 숨기고 지도+사이드바(PC식) 레이아웃 사용.
  useEffect(() => {
    const apply = () => {
      document.documentElement.classList.toggle('pad-mode', detectDeviceType() === 'pad');
    };
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, []);
  return (
    <AuthProvider>
      <GlobalStyle />
      <WakeLockKeeper />
      <BrowserRouter>
        <RnBridgeInit />
        <SiteProtection />
        <VersionChecker />
        <NavStateSync />
        <BackController />
        <LoginTransitionOverlay />
        <InAppToastHost />
        <Routes>
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/admin/*" element={<AdminRoute><AdminPage /></AdminRoute>} />
          <Route path="/properties" element={<PrivateRoute><AppLayout><PropertySearchPage /></AppLayout></PrivateRoute>} />
          <Route path="/utils" element={<PrivateRoute><AppLayout><UtilPage /></AppLayout></PrivateRoute>} />
          <Route path="/quest" element={<PrivateRoute><AppLayout><QuestPage /></AppLayout></PrivateRoute>} />
          <Route path="/my" element={<PrivateRoute><AppLayout><MyPage /></AppLayout></PrivateRoute>} />
          <Route path="/notices" element={<PrivateRoute><AppLayout><NoticePage /></AppLayout></PrivateRoute>} />
          <Route path="/chat" element={<PrivateRoute><AppLayout><ChatPage /></AppLayout></PrivateRoute>} />
          {IssueLabPage && <Route path="/issuelab" element={<PrivateRoute><Suspense fallback={null}><IssueLabPage /></Suspense></PrivateRoute>} />}
          <Route path="/*" element={<PrivateRoute><AppLayout><MainPage /></AppLayout></PrivateRoute>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
